<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import FormInput from '../../../components/Form/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { DEFAULT_REDIRECT_URL } from 'dashboard/constants/globals';
import { setNewPassword } from '../../../api/auth';

const hasUppercase = value => /[A-Z]/.test(value);
const hasLowercase = value => /[a-z]/.test(value);
const hasNumber = value => /[0-9]/.test(value);
const hasSpecial = value => /[!@#$%^&*()_+\-=[\]{}|'"/\\.,`<>:;?~]/.test(value);

export default {
  components: {
    FormInput,
    NextButton,
  },
  props: {
    resetPasswordToken: { type: String, default: '' },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      credentials: {
        confirmPassword: '',
        password: '',
      },
      newPasswordAPI: {
        message: '',
        showLoading: false,
      },
      error: '',
    };
  },
  computed: {
    passwordRules() {
      const pw = this.credentials.password;
      return [
        {
          key: 'minLength',
          met: pw.length >= 12,
          label: this.$t('SET_NEW_PASSWORD.RULES.MIN_LENGTH'),
        },
        {
          key: 'uppercase',
          met: hasUppercase(pw),
          label: this.$t('SET_NEW_PASSWORD.RULES.UPPERCASE'),
        },
        {
          key: 'lowercase',
          met: hasLowercase(pw),
          label: this.$t('SET_NEW_PASSWORD.RULES.LOWERCASE'),
        },
        {
          key: 'number',
          met: hasNumber(pw),
          label: this.$t('SET_NEW_PASSWORD.RULES.NUMBER'),
        },
        {
          key: 'special',
          met: hasSpecial(pw),
          label: this.$t('SET_NEW_PASSWORD.RULES.SPECIAL'),
        },
      ];
    },
    allRulesMet() {
      return this.passwordRules.every(r => r.met);
    },
    passwordsMatch() {
      return (
        this.credentials.confirmPassword.length > 0 &&
        this.credentials.password === this.credentials.confirmPassword
      );
    },
    isFormValid() {
      return (
        this.allRulesMet &&
        this.passwordsMatch &&
        !this.newPasswordAPI.showLoading
      );
    },
  },
  mounted() {
    if (!this.resetPasswordToken) {
      window.location = DEFAULT_REDIRECT_URL;
    }
  },
  validations: {
    credentials: {
      password: {
        required,
        minLength: minLength(12),
        hasUppercase,
        hasLowercase,
        hasNumber,
        hasSpecial,
      },
      confirmPassword: {
        required,
        isEqPassword(value) {
          return value === this.credentials.password;
        },
      },
    },
  },
  methods: {
    showAlertMessage(message) {
      this.newPasswordAPI.showLoading = false;
      useAlert(message);
    },
    submitForm() {
      this.v$.$touch();
      if (!this.isFormValid) return;

      this.newPasswordAPI.showLoading = true;
      const credentials = {
        confirmPassword: this.credentials.confirmPassword,
        password: this.credentials.password,
        resetPasswordToken: this.resetPasswordToken,
      };
      setNewPassword(credentials)
        .then(() => {
          window.location = DEFAULT_REDIRECT_URL;
        })
        .catch(error => {
          this.showAlertMessage(
            error?.message || this.$t('SET_NEW_PASSWORD.API.ERROR_MESSAGE')
          );
        });
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col justify-center w-full min-h-screen py-12 bg-n-brand/5 dark:bg-n-background sm:px-6 lg:px-8"
  >
    <form
      class="bg-white shadow sm:mx-auto sm:w-full sm:max-w-lg dark:bg-n-solid-2 p-11 sm:shadow-lg sm:rounded-lg"
      @submit.prevent="submitForm"
    >
      <h1
        class="mb-1 text-2xl font-medium tracking-tight text-left text-n-slate-12"
      >
        {{ $t('SET_NEW_PASSWORD.TITLE') }}
      </h1>

      <div class="space-y-4 mt-4">
        <FormInput
          v-model="credentials.password"
          name="password"
          type="password"
          :has-error="false"
          :placeholder="$t('SET_NEW_PASSWORD.PASSWORD.PLACEHOLDER')"
          @input="v$.credentials.password.$touch"
        />

        <!-- Password requirements checklist -->
        <ul class="pw-rules-list">
          <li
            v-for="rule in passwordRules"
            :key="rule.key"
            class="pw-rule"
            :class="{
              'pw-rule--met': rule.met && credentials.password.length > 0,
            }"
          >
            <span class="pw-rule-icon">
              <i
                :class="
                  rule.met && credentials.password.length > 0
                    ? 'i-lucide-circle-check-big'
                    : 'i-lucide-circle'
                "
              />
            </span>
            <span class="pw-rule-text">{{ rule.label }}</span>
          </li>
        </ul>

        <FormInput
          v-model="credentials.confirmPassword"
          name="confirm_password"
          type="password"
          :has-error="credentials.confirmPassword.length > 0 && !passwordsMatch"
          :error-message="$t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.ERROR')"
          :placeholder="$t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.PLACEHOLDER')"
          @input="v$.credentials.confirmPassword.$touch"
        />

        <!-- Match indicator -->
        <div
          v-if="credentials.confirmPassword.length > 0"
          class="pw-rule"
          :class="{ 'pw-rule--met': passwordsMatch }"
        >
          <span class="pw-rule-icon">
            <i
              :class="
                passwordsMatch ? 'i-lucide-circle-check-big' : 'i-lucide-circle'
              "
            />
          </span>
          <span class="pw-rule-text">
            {{ $t('SET_NEW_PASSWORD.RULES.MATCH') }}
          </span>
        </div>

        <NextButton
          lg
          type="submit"
          data-testid="submit_button"
          class="w-full"
          :label="$t('SET_NEW_PASSWORD.SUBMIT')"
          :disabled="!isFormValid"
          :is-loading="newPasswordAPI.showLoading"
        />
      </div>
    </form>
  </div>
</template>

<style scoped>
.pw-rules-list {
  @apply flex flex-col gap-1.5 list-none p-0 m-0;
}

.pw-rule {
  @apply flex items-center gap-2 text-xs text-n-slate-9
    transition-colors duration-200;
}

.pw-rule--met {
  @apply text-n-teal-11;
}

.pw-rule-icon {
  @apply text-sm shrink-0;
}

.pw-rule-text {
  @apply leading-tight;
}
</style>
